import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING_TYPES = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'flight', label: 'Flight' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'activity', label: 'Activity' },
  { value: 'dining', label: 'Dining' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
];

const RESERVATION_STATUSES = [
  { value: 'researching', label: 'Researching' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'held', label: 'Held' },
  { value: 'requested', label: 'Requested' },
  { value: 'booked', label: 'Booked' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAYMENT_STATUSES = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'cancelled', label: 'Cancelled' },
];

const VISIBILITY_OPTIONS = [
  { value: 'visible', label: 'Visible to Client' },
  { value: 'hidden', label: 'Hidden / Surprise' },
  { value: 'internal', label: 'Internal Only' },
];

export function BookingFormModal({ open, onClose, onSaved, booking = null, trips = [], clients = [] }) {
  const isEdit = !!booking;
  const [saving, setSaving] = useState(false);
  // Travelers fetched lazily once a trip (and therefore a client) is selected.  Keeps the
  // modal snappy when opened for the "Add" path on a booking unrelated to a household.
  const [householdTravelers, setHouseholdTravelers] = useState([]);
  const [travelersLoading, setTravelersLoading] = useState(false);
  const [form, setForm] = useState({
    trip_id: '', client_id: '', traveler_ids: [], booking_type: 'hotel', vendor: '',
    reservation_status: 'researching', confirmation_number: '',
    booking_date: '', start_date: '', end_date: '', due_date: '',
    quantity: 1, unit_detail: '', description: '', notes: '', internal_notes: '',
    client_visibility: 'visible',
    quoted_cost: 0, actual_cost: 0, currency: 'USD',
    deposit_amount: 0, balance_due: 0, amount_paid: 0,
    payment_status: 'unpaid', payable_vendor: '', receivable_client_charge: 0,
    auto_spawn_payment: false,
  });

  useEffect(() => {
    if (booking) {
      setForm({
        trip_id: booking.trip_id || '',
        client_id: booking.client_id || '',
        traveler_ids: booking.traveler_ids || [],
        booking_type: booking.booking_type || 'hotel',
        vendor: booking.vendor || '',
        reservation_status: booking.reservation_status || booking.status || 'researching',
        confirmation_number: booking.confirmation_number || '',
        booking_date: booking.booking_date?.split('T')[0] || '',
        start_date: booking.start_date?.split('T')[0] || '',
        end_date: booking.end_date?.split('T')[0] || '',
        due_date: booking.due_date?.split('T')[0] || '',
        quantity: booking.quantity || 1,
        unit_detail: booking.unit_detail || '',
        description: booking.description || '',
        notes: booking.notes || '',
        internal_notes: booking.internal_notes || '',
        client_visibility: booking.client_visibility || 'visible',
        quoted_cost: booking.quoted_cost || 0,
        actual_cost: booking.actual_cost || 0,
        currency: booking.currency || 'USD',
        deposit_amount: booking.deposit_amount || 0,
        balance_due: booking.balance_due || 0,
        amount_paid: booking.amount_paid || 0,
        payment_status: booking.payment_status || 'unpaid',
        payable_vendor: booking.payable_vendor || '',
        receivable_client_charge: booking.receivable_client_charge || 0,
        auto_spawn_payment: false, // edits never re-spawn — would duplicate payments
      });
    } else {
      setForm({
        trip_id: '', client_id: '', traveler_ids: [], booking_type: 'hotel', vendor: '',
        reservation_status: 'researching', confirmation_number: '',
        booking_date: '', start_date: '', end_date: '', due_date: '',
        quantity: 1, unit_detail: '', description: '', notes: '', internal_notes: '',
        client_visibility: 'visible',
        quoted_cost: 0, actual_cost: 0, currency: 'USD',
        deposit_amount: 0, balance_due: 0, amount_paid: 0,
        payment_status: 'unpaid', payable_vendor: '', receivable_client_charge: 0,
        auto_spawn_payment: false,
      });
    }
  }, [booking, open]);

  // Lazy-load travelers for the household whenever client_id changes (typically driven by
  // trip selection auto-populate).  Keeps the modal fast: no fetch if no trip is picked yet.
  useEffect(() => {
    const cid = form.client_id;
    if (!cid) { setHouseholdTravelers([]); return; }
    let cancelled = false;
    setTravelersLoading(true);
    axios.get(`${API}/travelers`, { params: { client_id: cid }, withCredentials: true })
      .then(res => { if (!cancelled) setHouseholdTravelers(res.data || []); })
      .catch(() => { if (!cancelled) setHouseholdTravelers([]); })
      .finally(() => { if (!cancelled) setTravelersLoading(false); });
    return () => { cancelled = true; };
  }, [form.client_id]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.trip_id) {
      toast.error('Please select a trip');
      return;
    }
    if (!form.vendor) {
      toast.error('Please enter a vendor name');
      return;
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error('End date must be on or after start date');
      return;
    }
    const cost = Number(form.actual_cost) || Number(form.quoted_cost) || 0;
    if (form.auto_spawn_payment && cost <= 0) {
      toast.error('Cannot spawn payment: cost is zero');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        // `auto_spawn_payment` is intentionally NOT sent on edits — would duplicate the row.
        const { auto_spawn_payment, ...payload } = form;
        await axios.patch(`${API}/bookings/${booking.id}`, payload);
        toast.success('Booking updated');
      } else {
        const res = await axios.post(`${API}/bookings`, form);
        if (res.data?.spawned_payment_id) {
          toast.success('Booking created · payment row added to AR');
        } else {
          toast.success('Booking created');
        }
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save booking');
    } finally {
      setSaving(false);
    }
  };

  const toggleTraveler = (id) => {
    setForm(prev => ({
      ...prev,
      traveler_ids: prev.traveler_ids.includes(id)
        ? prev.traveler_ids.filter(t => t !== id)
        : [...prev.traveler_ids, id],
    }));
  };

  // Auto-populate client when trip is selected
  const handleTripChange = (tripId) => {
    handleChange('trip_id', tripId);
    const trip = trips.find(t => t.id === tripId);
    if (trip?.client_id) {
      handleChange('client_id', trip.client_id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
        <DialogHeader>
          <DialogTitle className="font-serif text-[var(--vt-text)]">
            {isEdit ? 'Edit Booking' : 'Add Booking'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Trip & Client */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Trip *</Label>
              <Select value={form.trip_id} onValueChange={handleTripChange}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="booking-trip-select">
                  <SelectValue placeholder="Select trip" />
                </SelectTrigger>
                <SelectContent>
                  {trips.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.internal_trip_name || t.destinations || 'Untitled'} — {t.client_name || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Client</Label>
              <Select value={form.client_id} onValueChange={v => handleChange('client_id', v)}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="booking-client-select">
                  <SelectValue placeholder="Auto from trip" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Type, Vendor, Status */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Booking Type</Label>
              <Select value={form.booking_type} onValueChange={v => handleChange('booking_type', v)}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="booking-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BOOKING_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Vendor *</Label>
              <Input
                value={form.vendor}
                onChange={e => handleChange('vendor', e.target.value)}
                placeholder="e.g. Marriott, Delta"
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-vendor-input"
              />
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Reservation Status</Label>
              <Select value={form.reservation_status} onValueChange={v => handleChange('reservation_status', v)}>
                <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="booking-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESERVATION_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Confirmation & Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Confirmation / Reference #</Label>
              <Input
                value={form.confirmation_number}
                onChange={e => handleChange('confirmation_number', e.target.value)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-confirmation-input"
              />
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Booking Date</Label>
              <Input
                type="date"
                value={form.booking_date}
                onChange={e => handleChange('booking_date', e.target.value)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-date-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Service Start Date</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={e => handleChange('start_date', e.target.value)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-start-date"
              />
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Service End Date</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={e => handleChange('end_date', e.target.value)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-end-date"
              />
            </div>
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Payment Due Date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={e => handleChange('due_date', e.target.value)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-due-date"
              />
            </div>
          </div>

          {/* Quantity & Description */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-[var(--vt-text-muted)] text-xs">Qty</Label>
              <Input
                type="number"
                value={form.quantity}
                onChange={e => handleChange('quantity', parseInt(e.target.value) || 1)}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-quantity"
              />
            </div>
            <div className="col-span-3">
              <Label className="text-[var(--vt-text-muted)] text-xs">Unit Detail</Label>
              <Input
                value={form.unit_detail}
                onChange={e => handleChange('unit_detail', e.target.value)}
                placeholder="e.g. 3 nights, 2 adults"
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-unit-detail"
              />
            </div>
          </div>

          <div>
            <Label className="text-[var(--vt-text-muted)] text-xs">Description</Label>
            <Textarea
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              rows={2}
              className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
              data-testid="booking-description"
            />
          </div>

          {/* Financial Section */}
          <div className="border-t border-[var(--vt-border)] pt-4">
            <p className="text-xs font-medium text-[var(--vt-text-muted)] uppercase mb-3">Financial</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Quoted Cost</Label>
                <Input
                  type="number" step="0.01"
                  value={form.quoted_cost}
                  onChange={e => handleChange('quoted_cost', parseFloat(e.target.value) || 0)}
                  className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                  data-testid="booking-quoted-cost"
                />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Actual Cost</Label>
                <Input
                  type="number" step="0.01"
                  value={form.actual_cost}
                  onChange={e => handleChange('actual_cost', parseFloat(e.target.value) || 0)}
                  className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                  data-testid="booking-actual-cost"
                />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Currency</Label>
                <Input
                  value={form.currency}
                  onChange={e => handleChange('currency', e.target.value)}
                  className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                  data-testid="booking-currency"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Deposit</Label>
                <Input
                  type="number" step="0.01"
                  value={form.deposit_amount}
                  onChange={e => handleChange('deposit_amount', parseFloat(e.target.value) || 0)}
                  className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                  data-testid="booking-deposit"
                />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Amount Paid</Label>
                <Input
                  type="number" step="0.01"
                  value={form.amount_paid}
                  onChange={e => handleChange('amount_paid', parseFloat(e.target.value) || 0)}
                  className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                  data-testid="booking-amount-paid"
                />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Balance Due</Label>
                <Input
                  type="number" step="0.01"
                  value={form.balance_due}
                  onChange={e => handleChange('balance_due', parseFloat(e.target.value) || 0)}
                  className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                  data-testid="booking-balance-due"
                />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Payment Status</Label>
                <Select value={form.payment_status} onValueChange={v => handleChange('payment_status', v)}>
                  <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="booking-payment-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Payable To (Vendor)</Label>
                <Input
                  value={form.payable_vendor}
                  onChange={e => handleChange('payable_vendor', e.target.value)}
                  className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                  data-testid="booking-payable-vendor"
                />
              </div>
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Client Charge (Receivable)</Label>
                <Input
                  type="number" step="0.01"
                  value={form.receivable_client_charge}
                  onChange={e => handleChange('receivable_client_charge', parseFloat(e.target.value) || 0)}
                  className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                  data-testid="booking-client-charge"
                />
              </div>
            </div>
          </div>

          {/* Visibility & Notes */}
          <div className="border-t border-[var(--vt-border)] pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[var(--vt-text-muted)] text-xs">Client Visibility</Label>
                <Select value={form.client_visibility} onValueChange={v => handleChange('client_visibility', v)}>
                  <SelectTrigger className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="booking-visibility">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VISIBILITY_OPTIONS.map(v => (
                      <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-[var(--vt-text-muted)] text-xs">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={e => handleChange('notes', e.target.value)}
                rows={2}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-notes"
              />
            </div>
            <div className="mt-3">
              <Label className="text-[var(--vt-text-muted)] text-xs">Internal Notes</Label>
              <Textarea
                value={form.internal_notes}
                onChange={e => handleChange('internal_notes', e.target.value)}
                rows={2}
                className="bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
                data-testid="booking-internal-notes"
              />
            </div>
          </div>

          {/* Travelers attach */}
          {form.client_id && (
            <div className="border-t border-[var(--vt-border)] pt-4">
              <Label className="text-[var(--vt-text-muted)] text-xs">
                Attach travelers
                <span className="text-[var(--vt-text-muted)] ml-2 text-[10px]">
                  {form.traveler_ids.length} selected
                </span>
              </Label>
              {travelersLoading ? (
                <p className="text-xs text-[var(--vt-text-muted)] py-2">Loading travelers…</p>
              ) : householdTravelers.length === 0 ? (
                <p className="text-xs text-[var(--vt-text-muted)] py-2">No travelers on file for this client.</p>
              ) : (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="booking-traveler-chips">
                  {householdTravelers.map(t => {
                    const selected = form.traveler_ids.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => toggleTraveler(t.id)}
                        data-testid={`booking-traveler-${t.id}`}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                          selected
                            ? 'bg-[var(--vt-gold)] text-white border-[var(--vt-gold)]'
                            : 'border-[var(--vt-border)] text-[var(--vt-text-muted)] hover:border-[var(--vt-gold)] hover:text-[var(--vt-text)]'
                        }`}
                      >
                        {t.full_name || t.legal_full_name || t.preferred_name || 'Traveler'}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Auto-spawn payment row (only on create) */}
          {!isEdit && (
            <div className="border-t border-[var(--vt-border)] pt-4">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="booking-auto-spawn-label">
                <input
                  type="checkbox"
                  checked={form.auto_spawn_payment}
                  onChange={e => handleChange('auto_spawn_payment', e.target.checked)}
                  data-testid="booking-auto-spawn-checkbox"
                  className="mt-1"
                />
                <span>
                  <span className="text-sm text-[var(--vt-text)]">Add matching payment row to AR</span>
                  <span className="block text-xs text-[var(--vt-text-muted)] mt-0.5">
                    Drops a pending receivable into Money the moment this booking is saved.
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[var(--vt-border)]">
            <Button variant="ghost" onClick={onClose} data-testid="booking-cancel-btn">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[var(--vt-gold)] hover:bg-[var(--vt-gold)]/90 text-white"
              data-testid="booking-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEdit ? 'Update Booking' : 'Create Booking'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
